import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import GuideCard from '@/components/feature/GuideCard';
import { categories } from '@/mocks/categories';
import { getGuideBySlug, getGuideDetail, getRelatedGuides, riskMeta } from '@/lib/guideData';
import { useSeo, buildCanonical } from '@/hooks/useSeo';

export default function GuideDetailPage() {
  const { slug = '' } = useParams();
  const guide = getGuideBySlug(slug);
  const detail = getGuideDetail(slug);
  const related = getRelatedGuides(slug);

  const category = categories.find((c) => c.id === guide?.category);
  const risk = guide ? riskMeta[guide.risk] : riskMeta.mid;

  useSeo({
    title: guide ? `${guide.title}｜看料避雷社` : '避雷清单｜看料避雷社',
    description: guide
      ? `${guide.summary}（含风险等级、常见套路、危险信号与避雷清单）`
      : '查看看料避雷社的消费避雷清单。',
    keywords: guide ? guide.tags.join(',') : '避雷清单,消费避雷',
    path: `/guides/${slug}`,
    type: 'article',
    image: guide?.cover,
    jsonLd: guide
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: guide.title,
            description: guide.summary,
            image: guide.cover,
            datePublished: guide.date,
            dateModified: guide.date,
            inLanguage: 'zh-CN',
            keywords: guide.tags.join(', '),
            articleSection: category?.name,
            author: { '@type': 'Organization', name: '看料避雷社' },
            publisher: { '@type': 'Organization', name: '看料避雷社' },
            mainEntityOfPage: buildCanonical(`/guides/${slug}`),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: buildCanonical('/') },
              { '@type': 'ListItem', position: 2, name: '避雷清单', item: buildCanonical('/guides') },
              { '@type': 'ListItem', position: 3, name: guide.title, item: buildCanonical(`/guides/${slug}`) },
            ],
          },
        ]
      : undefined,
  });

  if (!guide || !detail) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="text-center max-w-md">
            <span className="w-16 h-16 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-3xl">
              <i className="ri-file-warning-line"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这份清单不存在或已被移动</h1>
            <p className="mt-3 text-sm text-foreground-600 font-body">
              可能是链接输错了，也可能清单正在修订。回到清单库重新找一份吧。
            </p>
            <Link
              to="/guides"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回清单库
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumbs
        items={[
          { name: '首页', to: '/' },
          { name: '避雷清单', to: '/guides' },
          { name: category?.name || '清单', to: `/guides?category=${guide.category}` },
          { name: guide.title },
        ]}
      />

      <main className="flex-1">
        <article>
          <header className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100/60 border-b border-background-200">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-label ${risk.className}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${risk.dot}`}></span>
                {risk.label}
              </span>
              {category && (
                <Link
                  to={`/guides?category=${category.id}`}
                  className="text-xs font-label text-foreground-600 hover:text-primary-700 rounded-full border border-background-300 px-3 py-1"
                >
                  {category.name}
                </Link>
              )}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight max-w-4xl">
              {guide.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed font-body max-w-3xl">
              {guide.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-foreground-500 font-label">
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>更新于 {guide.date}</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>约 {guide.readTime}</span>
              <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{guide.views} 次阅读</span>
            </div>
          </header>

          <div className="w-full h-64 md:h-96 overflow-hidden bg-background-200">
            <img
              src={guide.cover}
              alt={`${guide.title} 避雷清单配图`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8">
                <p className="text-base md:text-lg text-foreground-800 leading-loose font-body border-l-4 border-primary-400 pl-5">
                  {detail.intro}
                </p>

                <div className="mt-8 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4 md:p-5">
                  <span className="w-8 h-8 rounded-md bg-accent-500 text-background-50 flex items-center justify-center shrink-0">
                    <i className="ri-alarm-warning-line"></i>
                  </span>
                  <div>
                    <h2 className="font-heading text-sm font-bold text-accent-900">风险提示</h2>
                    <p className="mt-1 text-sm text-accent-800/90 leading-relaxed font-body">{detail.riskNote}</p>
                  </div>
                </div>

                {detail.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index}`} className="mt-10 scroll-mt-24">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-start gap-3">
                      <span className="font-heading text-accent-500/70 text-base md:text-lg pt-1">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4 pl-8 md:pl-9">
                      {section.paragraphs.map((p) => (
                        <p key={p} className="text-sm md:text-base text-foreground-700 leading-loose font-body">
                          {p}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}

                <section className="mt-12 rounded-lg border border-accent-200 bg-accent-50/70 p-5 md:p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-radar-line text-accent-600"></i>
                    危险信号
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600 font-body">
                    出现下面任何一条，都建议先停下来再确认一次。
                  </p>
                  <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {detail.signals.map((signal) => (
                      <li key={signal} className="flex items-start gap-2.5 rounded-md bg-background-50 border border-accent-200/70 px-3 py-2.5">
                        <i className="ri-flag-2-line text-accent-500 mt-0.5"></i>
                        <span className="text-sm text-foreground-800 font-body leading-relaxed">{signal}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="mt-8 rounded-lg border border-secondary-200 bg-secondary-50/70 p-5 md:p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-shield-check-line text-secondary-600"></i>
                    避雷清单
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600 font-body">
                    决定要买或要签的时候，照着这几步做一遍。
                  </p>
                  <ul className="mt-4 space-y-3">
                    {detail.checklist.map((item, index) => (
                      <li key={item} className="flex items-start gap-3 rounded-md bg-background-50 border border-secondary-200/70 px-4 py-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center text-xs shrink-0 font-label">
                          {index + 1}
                        </span>
                        <span className="text-sm text-foreground-800 font-body leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                  <nav className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-list-check text-primary-600"></i>
                      本页目录
                    </h3>
                    <ol className="space-y-2.5">
                      {detail.sections.map((section, index) => (
                        <li key={section.heading}>
                          <a
                            href={`#section-${index}`}
                            className="text-sm font-body text-foreground-600 hover:text-primary-700 transition-colors flex items-start gap-2"
                          >
                            <span className="font-label text-xs text-foreground-400 pt-0.5">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                            {section.heading}
                          </a>
                        </li>
                      ))}
                      <li>
                        <a href="#" className="text-sm font-body text-foreground-600 hover:text-primary-700 transition-colors flex items-start gap-2">
                          <span className="font-label text-xs text-foreground-400 pt-0.5">·</span>
                          危险信号与避雷清单
                        </a>
                      </li>
                    </ol>
                  </nav>

                  <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-price-tag-3-line text-accent-600"></i>
                      关键词
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {guide.tags.map((tag) => (
                        <Link
                          key={tag}
                          to={`/guides?q=${encodeURIComponent(tag)}`}
                          className="rounded-full bg-secondary-100 text-secondary-800 border border-secondary-200 px-3 py-1 text-xs font-label hover:bg-secondary-200 transition-colors"
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                    <h3 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
                      <i className="ri-notification-3-line text-primary-600"></i>
                      订阅更新提醒
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 font-body">
                      这份清单更新时，我们会发一封简短提醒给你。
                    </p>
                    <Link
                      to="/#subscribe"
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      去订阅
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100/60 border-t border-background-200">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关避雷清单</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <GuideCard key={item.slug} guide={item} />
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}